import React, { useEffect, useRef, useState } from 'react';

import { Box, Flex, ScrollArea } from '@mantine/core';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ChatMessageRenderer } from 'components/SmartAssistantComponents/ChatMessageRenderer';
import { SmartAssistantChatInput } from 'components/SmartAssistantComponents/SmartAssistantChatInput';
import { useSubscriptionContext } from 'contexts/SubscriptionContext';
import { ConversationHeader } from 'pages/ManageSmartAssistant/Sections/ConversationSections/ConversationsSection/ConversationHeader/ConversationHeader';
import { useTranslation } from 'react-i18next';
import { ConversationTakeoverService } from 'services/conversation-takeover-service';
import { NotificationService } from 'services/notification-service';
import { TAKEOVER_MESSAGE_MAX_LENGTH, TAKEOVER_WINDOW_DURATION_MS } from 'shared/constants/conversations';
import { EvaluationTestInfo } from 'shared/constants/evaluation';
import { CONVERSATION_TAKEOVER_QUERY_KEY } from 'shared/constants/query-keys';
import { CONVERSATION_VIEW_MAW } from 'shared/constants/theme';
import { ChatMessage } from 'shared/interfaces/ChatMessage';
import { Conversation, ConversationSourceType, TakeoverStatus } from 'shared/interfaces/Conversation';
import { SmartAssistant, SmartAssistantVisualConfig } from 'shared/interfaces/SmartAssistant';
import { CAN_TAKEOVER_SOURCE_TYPES } from 'shared/utils/conversation';
import { scrollToBottom } from 'shared/utils/navigation';

const TAKEOVER_POLLING_INTERVAL = 5000;

interface IndividualConversationViewProps {
  conversation: Conversation | undefined;
  smartAssistant: SmartAssistant;
  smartAssistantVisualConfig: SmartAssistantVisualConfig;
  onConversationUpdate: (updatedConversation: Conversation) => void;
  evaluationTestInfo?: EvaluationTestInfo;
  isGuest?: boolean;
}

export function IndividualConversationView({
  conversation,
  smartAssistant,
  smartAssistantVisualConfig,
  onConversationUpdate,
  evaluationTestInfo,
  isGuest = false
}: IndividualConversationViewProps) {
  const { t } = useTranslation();
  const [subscription] = useSubscriptionContext();
  const conversationViewport = useRef<HTMLDivElement>(null);

  // This is a temporary message to display while the send takeover message mutation is loading
  const [temporaryTakeoverMessage, setTemporaryTakeoverMessage] = useState<ChatMessage>({
    fromUser: false,
    text: ''
  });

  const canTakeover = conversation?.source.type && CAN_TAKEOVER_SOURCE_TYPES.includes(conversation.source.type);

  useQuery({
    queryKey: [CONVERSATION_TAKEOVER_QUERY_KEY, { conversationId: conversation?._id }],
    // undefined conversation id should never happen since query is enabled only if conversation is defined
    queryFn: () => ConversationTakeoverService.getConversationTakeoverById(conversation?._id ?? ''),
    onSuccess: (updatedConversation: Conversation) => {
      // If the conversation that we get is outside of the Meta reply window duration, we need to disable the takeover
      if (
        isPastMetaTakeoverWindow(updatedConversation) &&
        updatedConversation.takeoverData?.status === TakeoverStatus.ON
      ) {
        disableTakeoverMutation.mutate(updatedConversation._id);
      } else {
        // We only want to trigger the update if the conversation has new messages
        const hasNewMessages = !conversation || updatedConversation.messages.length !== conversation.messages.length;

        if (hasNewMessages) onConversationUpdate(updatedConversation);
      }
    },
    enabled:
      !!subscription.product.enableTakeover &&
      !!conversation?._id &&
      canTakeover &&
      conversation.takeoverData?.status === TakeoverStatus.ON &&
      !isGuest,
    refetchInterval: TAKEOVER_POLLING_INTERVAL,
    notifyOnChangeProps: [] // We don't want to trigger a re-render when the conversation is fetched, only when new messages are added
  });

  const disableTakeoverMutation = useMutation({
    mutationFn: (conversationId: string) =>
      ConversationTakeoverService.updateTakeoverData({
        conversationId,
        takeoverStatus: TakeoverStatus.OFF
      }),
    onSuccess: (updatedConversation: Conversation) => onConversationUpdate(updatedConversation)
  });

  const sendTakeoverMessageMutation = useMutation({
    mutationFn: ConversationTakeoverService.sendTakeoverMessage,
    onSuccess: (updatedConversation: Conversation) => {
      onConversationUpdate(updatedConversation);
      setTemporaryTakeoverMessage((message) => ({ ...message, text: '' }));
    },
    onError: () => {
      setTemporaryTakeoverMessage((message) => ({ ...message, text: '' }));
      NotificationService.showResult(false, t('FEED_BACK.CONVERSATION.FAILURE.SEND_TAKEOVER_MESSAGE'));
    }
  });

  useEffect(() => {
    scrollToBottom(conversationViewport);
  }, [conversation, conversationViewport, temporaryTakeoverMessage]);

  const isPastMetaTakeoverWindow = (conversation: Conversation) => {
    // This restriction only applies to Facebook and Instagram
    if (
      conversation?.source.type !== ConversationSourceType.FACEBOOK &&
      conversation?.source.type !== ConversationSourceType.INSTAGRAM
    )
      return false;

    const nowMs = Date.now();
    const conversationLatestUpdateMs = conversation?.updatedAt || conversation?.createdAt || 0;

    const isPastTakeoverWindow = nowMs - conversationLatestUpdateMs >= TAKEOVER_WINDOW_DURATION_MS;

    return isPastTakeoverWindow;
  };

  return (
    <Flex direction="column" justify="space-between" h="100%" w="100%">
      <ScrollArea
        viewportRef={conversationViewport}
        h="100%"
        w="100%"
        pl="xs"
        sx={{
          '.mantine-ScrollArea-viewport': {
            paddingBottom: 0,
            '& > div': {
              display: 'table !important'
            }
          }
        }}>
        <Box w="100%" px="md" sx={{ position: 'sticky', top: 0, zIndex: 1 }}>
          {conversation && (
            <ConversationHeader
              conversation={conversation}
              smartAssistant={smartAssistant}
              onConversationUpdate={onConversationUpdate}
              evaluationTestInfo={evaluationTestInfo}
              takeOverData={{
                canTakeOver: canTakeover,
                isPastMetaTakeoverWindow: isPastMetaTakeoverWindow(conversation)
              }}
              isGuest={isGuest}
            />
          )}
        </Box>
        <Box maw={CONVERSATION_VIEW_MAW} ml="auto" mr="auto" px="lg">
          {conversation?.messages.map((message, index) => (
            <ChatMessageRenderer
              key={`${conversation._id}-${index}`}
              message={message}
              smartAssistantVisualConfig={smartAssistantVisualConfig}
              toolConfigs={smartAssistant?.toolConfigs}
              viewMode="readonly"
            />
          ))}
          {temporaryTakeoverMessage.text && (
            <Box sx={{ opacity: 0.5 }}>
              <ChatMessageRenderer
                message={temporaryTakeoverMessage}
                smartAssistantVisualConfig={smartAssistantVisualConfig}
                viewMode="readonly"
              />
            </Box>
          )}
        </Box>
      </ScrollArea>
      {canTakeover && !isGuest && (
        <Box pb={5} maw={CONVERSATION_VIEW_MAW} mx="auto" w="100%" px="lg">
          <SmartAssistantChatInput
            smartAssistantVisualConfig={smartAssistantVisualConfig}
            onSend={(value) => {
              // For now, we don't support sending images or audio in takeover messages
              if (!conversation || !value.text) return;
              sendTakeoverMessageMutation.mutate({
                conversationId: conversation._id,
                message: value.text
              });
              setTemporaryTakeoverMessage((message) => ({ ...message, text: value.text }));
            }}
            isSendDisabled={
              conversation?.takeoverData?.status !== TakeoverStatus.ON || sendTakeoverMessageMutation.isLoading
            }
            isInputDisabled={
              conversation?.takeoverData?.status !== TakeoverStatus.ON || !subscription.product.enableTakeover
            }
            maxCharacterCount={TAKEOVER_MESSAGE_MAX_LENGTH[conversation?.source.type]}
          />
        </Box>
      )}
    </Flex>
  );
}
